import * as React from 'react';

import { cn, Select } from '@rematter/ui-web';

import { EntitySelectProps } from './types';

import { createQueryHook } from '~/shared/providers/api';

export const useTerminalsQuery = createQueryHook(
  'shared.selectTerminal',
  (params: { portId?: string | null }) => ({
    port: {
      __args: {
        id: params.portId || '',
      },
      terminals: {
        id: true,
        terminalCode: true,
        name: true,
      },
    },
  }),
  {
    select: (data) => data?.port?.terminals ?? [],
  },
);

export type SelectableTerminal = NonNullable<
  ReturnType<typeof useTerminalsQuery>['data']
>[number];

export const SelectTerminal = ({
  onChange,
  value,
  portId,
  labelAdornment,
  selectedTerminal,
  ...props
}: EntitySelectProps<SelectableTerminal> & {
  portId?: string | null;
  labelAdornment?: React.ReactNode;
  selectedTerminal?: { id: string; name: string; terminalCode: string } | null;
}) => {
  const {
    data: terminals,
    isLoading,
    isFetching,
    isFetched,
  } = useTerminalsQuery({
    portId: portId || null,
    queryOptions: {
      enabled: !!portId,
      staleTime: 0, // Always fetch fresh data
    },
  });

  // Merge terminals from query with selected terminal to ensure it's always shown
  const allTerminals = React.useMemo(() => {
    const terminalMap = new Map<
      string,
      { id: string; name: string; terminalCode: string }
    >();

    // Add terminals from query
    terminals?.forEach((t) => terminalMap.set(t.id, t));

    // Add selected terminal if it exists and is not already in the list
    if (selectedTerminal && value === selectedTerminal.id) {
      terminalMap.set(selectedTerminal.id, selectedTerminal);
    }

    return Array.from(terminalMap.values());
  }, [terminals, selectedTerminal, value]);

  const options = React.useMemo(
    () =>
      allTerminals.map((terminal) => ({
        key: terminal.id,
        label: `${terminal.name}・${terminal.terminalCode}`,
      })),
    [allTerminals],
  );

  const handleChange = React.useCallback(
    (value: string | undefined) => {
      const terminal = allTerminals.find((it) => it.id === value);
      if (terminal) {
        onChange?.(value, terminal as SelectableTerminal);
      } else {
        onChange?.(value, undefined as never);
      }
    },
    [allTerminals, onChange],
  );

  React.useEffect(() => {
    if (value && !terminals?.find((it) => it.id === value)) {
      onChange?.(undefined, undefined as never);
    }
    if (!value && terminals?.length === 1) {
      onChange?.(terminals[0].id, terminals[0] as SelectableTerminal);
    }
  }, [value, terminals, onChange, portId]);

  const noTerminalsFound = !!portId && isFetched && !allTerminals?.length;

  return (
    <div className="@SelectTerminal flex flex-col gap-1">
      {props.label && labelAdornment && (
        <div className="flex items-center justify-between gap-2">
          <label className="text-sm font-medium whitespace-nowrap text-neutral/90">
            {props.label}
          </label>
          {labelAdornment}
        </div>
      )}
      <Select
        {...props}
        label={labelAdornment ? undefined : props.label}
        isRequired={
          labelAdornment ? false : props.isRequired && !noTerminalsFound
        }
        value={value || undefined}
        onChange={handleChange}
        className={cn('@SelectTerminal', props.className)}
        placeholder={noTerminalsFound ? 'No terminals found' : undefined}
        options={options}
        isLoading={isLoading || isFetching}
        isDisabled={props.isDisabled || !portId || noTerminalsFound}
      />
    </div>
  );
};
